/* @flow */

import * as React from 'react';
import styled from '@emotion/styled';
import numeral from 'numeral';
import { css } from 'glamor';
import { mix, rgba } from 'polished';

import { linearGradient } from 'app/utils/cssUtils';

type Props = {
  className?: string,
  label: string,
  maximum: number,
  score: number,
  isSmall?: boolean
};

const expandVertical = css.keyframes({
  from: { transform: 'scaleY(0)' },
  to: { transform: 'scaleY(1)' }
});

const Box = styled.div({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  height: 160
});

const BoxBar = styled.div(({ theme }) => ({
  backgroundColor: rgba(theme.color.basis5, 0.25),
  borderRadius: 2,
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1,
  position: 'relative',
  width: 4,
  height: '100%'
}));

const BoxBarInner = styled.div(({ maximum, theme, value, isSmall }) => {
  const stopValue = Math.min(value / maximum, 1);
  const stopColor = mix(stopValue, theme.color.widgetSecondary, theme.color.widgetPrimary);

  return {
    position: 'absolute',
    animationDuration: '1s',
    animationName: expandVertical,
    backgroundImage: linearGradient(
      'to top',
      theme.color.widgetPrimary,
      `${stopColor} ${100 * stopValue}%`,
      `transparent ${100 * stopValue}%`
    ),
    borderRadius: 2,
    display: 'block',
    height: '100%',
    transformOrigin: '50% 100%',
    width: 4
  };
});

const BoxLabel = styled.div(({ theme, isSmall }) => ({
  flexBasis: 'auto',
  flexGrow: 0,
  flexShrink: 0,
  fontSize: isSmall ? '0.6em' : theme.fontSize.tiny,
  textAlign: 'center'
}));

const BoxScore = styled.div(({ theme, isSmall }) => ({
  color: theme.color.widgetPrimary,
  flexBasis: 'auto',
  flexGrow: 0,
  flexShrink: 0,
  fontWeight: theme.fontWeight.demiBold,
  textAlign: 'center',
  fontSize: isSmall ? '0.8em' : 'auto'
}));

function FormBox({ label, maximum, score, className, isSmall }: Props) {
  return (
    <Box className={className}>
      <BoxBar>
        <BoxBarInner value={score} maximum={maximum} />
      </BoxBar>
      <BoxScore isSmall={isSmall}>{numeral(score).format('0,0.[0]')}</BoxScore>
      <BoxLabel isSmall={isSmall}>{label}</BoxLabel>
    </Box>
  );
}

FormBox.defaultProps = {
  className: undefined,
  isSmall: false
};

export default FormBox;
